import React from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'

import { RootState } from 'store'
import { UserRoles } from 'modules/UsersModule/types'
import { useBillingQuery } from 'hooks'
import { useGetSubscriptionResource } from 'api/subscription/getSubscriptionResource'
import SubscriptionDetailsTiles from 'components/SubscriptionDetailsTiles'
import { PaymentForm } from './components'
import { Header } from 'ui-v2'

const stripe = loadStripe(import.meta.env.VITE_STRIPE_KEY)

const PaymentRequiredContainer: React.FC = () => {
  const { profile } = useSelector((state: RootState) => state.auth)

  const isOwnerOrTravelAgent = profile?.role === UserRoles.Owner || profile?.role === UserRoles.TravelAgent
  const { data: billing, isLoading: isBillingLoading } = useBillingQuery({ enabled: isOwnerOrTravelAgent })
  const subscriptionResource = useGetSubscriptionResource({ queryOptions: { enabled: isOwnerOrTravelAgent } })
  const subscriptionData = subscriptionResource.data?.data

  const isTrialEnd = !!profile?.trial_ends_at && !subscriptionData?.payment_method?.card_last_four
  const isLoading = isBillingLoading || subscriptionResource.isLoading

  const title = isLoading
    ? ''
    : !isOwnerOrTravelAgent
    ? 'Access denied'
    : isTrialEnd
    ? 'Trial has expired'
    : 'Add payment method to start using Rate Cleaner'
  // const infoText = isLoading
  //   ? ''
  //   : !isOwnerOrTravelAgent
  //   ? 'Inform the owner and take full advantage of RateCleaner.'
  //   : isTrialEnd
  //   ? 'Your trial period has ended.\nComplete your payment method and take full advantage of RateCleaner.'
  //   : 'Payment processing problem.\nComplete your payment method and take full advantage of RateCleaner.'

  const trialEndsAt = subscriptionData?.trial_ends_at
  const subscription = subscriptionData?.subscription

  return (
    <div>
      <Header title={title} />

      <div className="relative mx-2 my-4 rounded-lg md:mx-7 p-2 bg-white md:p-6">
        {isLoading && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-50 opacity-75">
            <span className="absolute top-36 left-1/2 w-12 h-12 inline-flex items-center justify-center -mt-6 -ml-6">
              <svg
                aria-hidden="true"
                className="w-10 h-10 text-gray-200 animate-spin dark:text-gray-600 fill-blue-800"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </span>
          </div>
        )}

        <div className="flex flex-col gap-2 md:gap-4">
          <div className="flex flex-col-reverse items-start gap-2 md:gap-4 lg:flex-row">
            {isOwnerOrTravelAgent && (
              <div className="w-full lg:w-1/2 p-2 bg-bg-light rounded-md flex flex-col gap-2 md:gap-4 md:p-6">
                {trialEndsAt && (
                  <div className="text-base font-normal text-gray-800">
                    Subscription plan: <span className="font-semibold">Trial</span>
                    <br />
                    Active until <span className="font-semibold">{moment(trialEndsAt).format('MMMM D, YYYY')}</span>
                  </div>
                )}

                {subscription && !subscription.active && (
                  <Elements
                    options={{
                      locale: 'en',
                      mode: 'subscription',
                      amount: subscription.amount || 100,
                      currency: 'usd',
                      setupFutureUsage: 'off_session',
                      appearance: {
                        theme: 'stripe',
                      },
                    }}
                    stripe={stripe}
                  >
                    <PaymentForm initialBillingFormValues={billing} />
                  </Elements>
                )}
              </div>
            )}

            <div className="w-full lg:w-1/2 p-2 bg-bg-light rounded-md flex flex-col gap-2 md:gap-4 md:p-6">
              <SubscriptionDetailsTiles
                subscription={subscription}
                isTravelAgency={profile?.role === UserRoles.TravelAgent}
                isViewedByAdmin={false}
                trialEndsAtDate={trialEndsAt ? moment(trialEndsAt) : null}
                isShortVersion
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentRequiredContainer
